import { Link } from 'react-router-dom';
import { ROUTES, LIVE_SURFACES } from '@/config/routes';
import { cn } from '@/lib/cn';

type Surface = 'app' | 'vip' | 'tv';

const ALL_SURFACES: {
  id: Surface;
  /** Public suffix; empty for primary consumer surface (HOTTR. only). */
  suffix: string;
  to: string;
}[] = [
  { id: 'app', suffix: '', to: ROUTES.feed },
  // Cross-surface entry into .vip skips the landing page and drops users
  // straight into the roster — there's nothing on /vip they need to see
  // first when they're already inside HOTTR.
  { id: 'vip', suffix: 'vip', to: ROUTES.vip.roster },
  { id: 'tv', suffix: 'tv', to: ROUTES.tv.browse },
];

// Only surfaces that are live in production show a pill. `.vip`/`.tv` are
// hidden until their backends ship (see LIVE_SURFACES in config/routes).
const SURFACES = ALL_SURFACES.filter((s) => LIVE_SURFACES[s.id]);

interface AppSwitcherProps {
  /** Which surface the current shell represents. */
  active: Surface;
  /** Tight (toolbar inline) vs open (mobile row). */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Cross-surface app switcher — primary consumer is `HOTTR.` (no .app suffix);
 * other live surfaces render `HOTTR.vip` / `HOTTR.tv`. Poppins black, pink
 * fullstop, one logo unit per pill.
 */
export const AppSwitcher = ({ active, size = 'sm', className }: AppSwitcherProps) => (
  <nav
    aria-label="HOTTR surface"
    className={cn(
      'inline-flex items-center border border-white/[0.10] bg-charcoal/40 backdrop-blur',
      className,
    )}
  >
    {SURFACES.map((s) => {
      const isActive = s.id === active;
      // Pills stretch to fill the parent height (which is bumped to ~44px
      // on the mobile cross-surface row inside PageShell). On desktop, the
      // parent is the natural inline-flex container so we fall back to
      // intrinsic padding.
      const cell = size === 'sm' ? 'px-2.5 py-1.5' : 'px-3 py-2';
      const text = size === 'sm' ? 'text-[10px]' : 'text-[11px]';
      const label = s.suffix ? `HOTTR.${s.suffix}` : 'HOTTR.';
      return (
        <Link
          key={s.id}
          to={s.to}
          aria-current={isActive ? 'page' : undefined}
          aria-label={label}
          className={cn(
            'group inline-flex h-full flex-1 items-center justify-center',
            size === 'sm' ? 'min-w-[72px]' : 'min-w-[80px]',
            cell,
            'font-display font-black uppercase leading-none',
            'tracking-[-0.04em]',
            'border-r border-white/[0.08] last:border-r-0',
            'transition-[background,color] duration-fast ease-signal',
            isActive
              ? 'bg-pink/15 text-pink shadow-glow-soft'
              : 'text-text-secondary hover:bg-white/[0.04] hover:text-text-primary',
          )}
        >
          <span className={cn(text, 'tracking-widest')}>HOTTR</span>
          <span className={cn(text, 'text-pink')}>.</span>
          {s.suffix ? (
            <span className={cn(text, 'normal-case')}>{s.suffix}</span>
          ) : null}
        </Link>
      );
    })}
  </nav>
);
